import MatchDisplaySettings from "../components/match/MatchDisplaySettings";
import ZoomRulesAdmin from "../components/admin/ZoomRulesAdmin";
import AdminDashboard, {AdminOverview, AdminTool, AdminLinks} from "../components/admin/AdminDashboard";
import SanadAdmin, { TutorialsAdmin } from "../components/ContentSanadAdmin";
import WeeklyChallengesAdmin from "../components/admin/WeeklyChallengesAdmin";
import { RRSimulator, DrawManagement } from "../components/admin/ReleaseTools";
import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { 
  AlertCircle, ScrollText, Trophy, Shield, Sparkles, Crown, UserCheck, UserX, 
  Search, Edit3, Mail, KeyRound, X, Zap, Plus, Image as ImageIcon, Gamepad2, 
  Power, Bot, ShieldAlert, Download, Trash2, Ban, FolderArchive, Clock, RefreshCw, Monitor, Coins
} from "lucide-react";
import { toast } from "sonner";
import BannerManager from "../components/admin/BannerManager";

// ==============================================================================
// ⚖️ مودال الأدمن لتعديل النتيجة وسلب/منح النقاط (24 ساعة)
// ==============================================================================
export function AdminOverrideMatchModal({ match, onClose, onSaved }) {
  const [newWinnerId, setNewWinnerId] = useState(match.winner_clan_id || "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const operationId = useRef(null);
  if (!operationId.current) operationId.current = crypto.randomUUID();

  const submit = async (e) => {
    e.preventDefault();
    if (!newWinnerId) return toast.error("يرجى اختيار الكلان الفائز الجديد");
    if (newWinnerId === match.winner_clan_id) {
      toast.info("لم تقم بتغيير الفائز الحالي");
      return onClose();
    }

    setBusy(true);
    try {
      const { data } = await api.post(`/admin/matches/${match.id}/override-result`, {
        new_winner_clan_id: newWinnerId,
        expected_winner_clan_id: match.winner_clan_id,
        operation_id: operationId.current
      });
      setResult(data);
      toast.success("تم تصحيح النتيجة ونقاط الطرفين بنجاح");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "حدث خطأ أثناء تعديل النتيجة");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 backdrop-blur-md p-4 animate-in fade-in duration-200">
      <form onSubmit={submit} className="bg-[#141417] border border-white/10 rounded-2xl p-6 w-full max-w-md space-y-4 shadow-2xl relative" data-testid="override-match-form">
        <button type="button" onClick={onClose} className="absolute top-4 left-4 text-white/50 hover:text-white transition">
          <X size={20} />
        </button>

        <h2 className="font-display font-black text-xl text-gold-500 flex items-center gap-2">
          <RefreshCw size={20} /> تعديل نتيجة المباراة وسلب النقاط
        </h2>

        <div className="text-xs text-white/60 bg-white/5 p-3 rounded-xl border border-white/10 space-y-1">
          <div><span className="text-white/40">المباراة:</span> {match.clan_a?.name} vs {match.clan_b?.name}</div>
          <div><span className="text-white/40">الفائز الحالي:</span> <span className="text-gold-500 font-bold">{match.winner_clan_id === match.clan_a_id ? match.clan_a?.name : match.clan_b?.name}</span></div>
        </div>

        {result ? <div data-testid="correction-result" className="space-y-3" aria-live="polite">
          <p className="text-sm text-emerald-400">تم التصحيح. صافي التغيير الفعلي لكل كلان:</p>
          {(result.clans || []).map(row => <div key={row.id} className="bg-white/5 rounded-xl p-3 text-sm space-y-1">
            <strong>{row.id === match.clan_a_id ? match.clan_a?.name : match.clan_b?.name}</strong>
            <div dir="ltr">{row.before} → {row.after} RR ({row.delta > 0 ? "+" : ""}{row.delta})</div>
            <div className="text-xs text-white/60">إلغاء الأثر السابق: {row.undo_delta}، تطبيق النتيجة المصححة: {row.corrected_result_delta}</div>
            <div className="text-xs">نقاط الدوري: {row.points_delta > 0 ? "+" : ""}{row.points_delta}</div>
          </div>)}
          <button type="button" onClick={onClose} className="px-4 py-2 bg-gold-500 text-black rounded-xl">إغلاق</button>
        </div> : <>

        <div>
          <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block font-bold">اختر الفائز الجديد مع تعديل النقاط:</label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => { setNewWinnerId(match.clan_a_id); operationId.current = crypto.randomUUID(); }}
              className={`p-3 rounded-xl border text-sm font-bold flex flex-col items-center gap-1 transition ${
                newWinnerId === match.clan_a_id 
                  ? "bg-gold-500/20 text-gold-500 border-gold-500 shadow-[0_0_12px_rgba(255,204,0,0.3)]" 
                  : "bg-black/30 border-white/10 text-white/70 hover:border-white/30"
              }`}
            >
              <span>{match.clan_a?.name}</span>
              <span className="text-[10px] text-white/40 font-mono">[{match.clan_a?.tag}]</span>
            </button>

            <button
              type="button"
              disabled={busy}
              onClick={() => { setNewWinnerId(match.clan_b_id); operationId.current = crypto.randomUUID(); }}
              className={`p-3 rounded-xl border text-sm font-bold flex flex-col items-center gap-1 transition ${
                newWinnerId === match.clan_b_id 
                  ? "bg-gold-500/20 text-gold-500 border-gold-500 shadow-[0_0_12px_rgba(255,204,0,0.3)]" 
                  : "bg-black/30 border-white/10 text-white/70 hover:border-white/30"
              }`}
            >
              <span>{match.clan_b?.name}</span>
              <span className="text-[10px] text-white/40 font-mono">[{match.clan_b?.tag}]</span>
            </button>
          </div>
        </div>

        <p className="text-[11px] text-amber-300/80 bg-amber-500/10 p-2.5 rounded-xl border border-amber-500/20 leading-relaxed">
          ⚠️ سيؤدي هذا الإجراء إلى سلب نقاط الفوز من الكلان واللاعبين الحاضرين الخاسرين، ومنحها للكلان واللاعبين الفائزين الجدد تلقائياً.
        </p>

        <div className="flex gap-2 justify-end pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl hover:bg-white/5 text-gray-300 text-xs font-bold transition">
            إلغاء
          </button>
          <button disabled={busy} type="submit" className="px-5 py-2.5 rounded-xl bg-gold-500 text-black font-black hover:bg-gold-400 text-xs transition shadow-md disabled:opacity-50">
            {busy ? "جاري الحفظ..." : "تأكيد تعديل النتيجة"}
          </button>
        </div>
        </>}
      </form>
    </div>
  );
}

function EditUserModal({ target, onClose, onSaved }) {
  const [form, setForm] = useState({
    username: target.username || "",
    email: target.email || "",
    password: "",
    act: target.act || "",
    gaming_platform: target.gaming_platform || "pc",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const payload = {};
    if (form.username !== target.username) payload.username = form.username;
    if (form.email !== target.email) payload.email = form.email;
    if (form.password) payload.password = form.password;
    if (form.act !== (target.act || "")) payload.act = form.act;
    if (form.gaming_platform !== (target.gaming_platform || "pc")) payload.gaming_platform = form.gaming_platform;

    if (Object.keys(payload).length === 0) {
      toast.info("لا تغييرات");
      return onClose();
    }
    setBusy(true);
    try {
      await api.put(`/admin/users/${target.id}`, payload);
      toast.success("تم تحديث المستخدم بنجاح");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 backdrop-blur-md p-4 animate-in fade-in duration-200">
      <form onSubmit={submit} className="bg-[#141417] border border-white/10 rounded-2xl p-6 w-full max-w-md space-y-3.5 shadow-2xl relative" data-testid="edit-user-form">
        <div className="flex items-center justify-between pb-2 border-b border-white/10">
          <h2 className="font-display font-black text-lg text-white flex items-center gap-2">
            <Edit3 size={18} className="text-gold-500" /> تعديل بيانات {target.username}
          </h2>
          <button type="button" onClick={onClose} className="p-1 rounded-lg hover:bg-white/5 text-white/50 hover:text-white transition"><X size={16} /></button>
        </div>

        <div>
          <label className="text-xs font-bold text-white/70 mb-1 block">اسم المستخدم</label>
          <input data-testid="edit-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 outline-none focus:border-gold-500/50 text-sm text-white" />
        </div>

        <div>
          <label className="text-xs font-bold text-white/70 mb-1 block">البريد الإلكتروني</label>
          <input data-testid="edit-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 outline-none focus:border-gold-500/50 text-sm text-white" />
        </div>

        <div>
          <label className="text-xs font-bold text-white/70 mb-1 block">منصة اللعب الأساسية (Gaming Platform)</label>
          <select
            value={form.gaming_platform}
            onChange={(e) => setForm({ ...form, gaming_platform: e.target.value })}
            className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 outline-none text-sm text-white focus:border-gold-500/50"
          >
            <option value="pc">🖥️ PC (كمبيوتر - يتطلب RivalsGuard)</option>
            <option value="ps5">🎮 PlayStation 5</option>
            <option value="xbox">🎮 Xbox Series X/S</option>
          </select>
        </div>

        <div>
          <label className="text-xs font-bold text-white/70 mb-1 block">Activision ID / Game ID</label>
          <input data-testid="edit-act" value={form.act} onChange={(e) => setForm({ ...form, act: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 outline-none focus:border-gold-500/50 text-sm text-white font-mono" />
        </div>

        <div>
          <label className="text-xs font-bold text-white/70 mb-1 block">كلمة مرور جديدة (اختياري)</label>
          <input data-testid="edit-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="اتركها فارغة لعدم التغيير" className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 outline-none focus:border-gold-500/50 text-sm text-white" />
        </div>

        <button data-testid="edit-user-submit" disabled={busy} type="submit" className="w-full py-2.5 rounded-xl bg-gold-500 text-black font-black hover:bg-gold-400 disabled:opacity-50 text-xs shadow-md transition mt-2">
          {busy ? "جاري الحفظ..." : "حفظ التغييرات"}
        </button>
      </form>
    </div>
  );
}

function UserRoleManager() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [editTarget, setEditTarget] = useState(null);
  const isOwner = user?.role === "owner";
  const canManageUsers = ["owner", "admin"].includes(user?.role);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState("");

  const load = useCallback(async () => {
    if (!canManageUsers) {
      setUsers([]);
      return;
    }
    setLoadingUsers(true); setUsersError("");
    try {
      const { data } = await api.get("/admin/users");
      if (!Array.isArray(data)) throw new Error("Invalid users response");
      setUsers(data);
    } catch {
      setUsersError("تعذر تحميل قائمة اللاعبين. أعد المحاولة؛ البحث يعمل بعد تحميل القائمة.");
    } finally { setLoadingUsers(false); }
  }, [canManageUsers]);

  useEffect(() => { load(); }, [load]);

  const setRole = async (uid, role) => {
    try {
      await api.post(`/admin/users/${uid}/role`, { role });
      toast.success(role === "admin" ? "تم ترقيته لمنظم" : "تم خفض دوره");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const filtered = users.filter((u) =>
    !q.trim() || u.username?.toLowerCase().includes(q.trim().toLowerCase()) || u.email?.toLowerCase().includes(q.trim().toLowerCase())
  );

  return (
    <section>
      <h2 className="font-display font-black text-xl mb-3 flex items-center gap-2 text-white">
        <UserCheck className="text-gold-500" /> إدارة اللاعبين {isOwner && <span className="text-[10px] uppercase font-bold tracking-widest bg-gold-500/10 text-gold-500 border border-gold-500/30 px-2 py-0.5 rounded-full">المالك فقط</span>}
      </h2>
      {loadingUsers && <p role="status" className="text-xs text-white/50 mb-3">جاري تحميل اللاعبين…</p>}
      {usersError && <div role="alert" className="text-xs text-rose-300 mb-3 flex flex-wrap gap-3 items-center">{usersError}<button type="button" onClick={load} className="underline">إعادة المحاولة</button></div>}
      <div className="relative max-w-md mb-3">
        <Search size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/30" />
        <input
          data-testid="users-search"
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="ابحث بالاسم أو البريد..."
          className="w-full bg-[#111113]/80 border border-white/10 rounded-xl pr-10 pl-4 py-2.5 outline-none focus:border-gold-500/40 text-sm text-white"
        />
      </div>
      <div className="bg-[#111113]/90 border border-white/10 rounded-2xl divide-y divide-white/5 max-h-[450px] overflow-y-auto shadow-xl">
        {filtered.map((u) => (
          <div key={u.id} data-testid={`user-row-${u.id}`} className="p-3.5 flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gold-500/10 border border-gold-500/20 grid place-items-center text-gold-500 font-display font-black text-sm shrink-0">
              {u.username[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate flex items-center gap-1.5 text-white text-sm">
                {u.username}
                {u.role === "owner" && <Crown size={13} className="text-gold-500 shrink-0" />}
                {u.is_plus && <Sparkles size={11} className="text-[#d4af37] shrink-0" />}
              </div>
              <div className="text-[11px] text-white/40 truncate mt-0.5">
                {u.email}{u.act ? ` • ${u.act}` : ""} • <span className="text-gold-500 uppercase font-mono">{u.gaming_platform || "PC"}</span>
              </div>
            </div>
            <div className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-white/5 border border-white/10 text-white/60">
              {u.role === "owner" ? "مالك" : u.role === "admin" ? "منظم" : "لاعب"}
            </div>
            {u.role !== "owner" && (
              <button data-testid={`edit-user-${u.id}`} onClick={() => setEditTarget(u)} className="px-2.5 py-1 rounded-lg text-xs bg-white/5 border border-white/10 hover:bg-white/10 text-white font-bold flex items-center gap-1 transition">
                <Edit3 size={12} /> تعديل
              </button>
            )}
            {isOwner && u.role !== "owner" && (
              <div className="flex gap-1">
                {u.role === "admin" ? (
                  <button data-testid={`demote-${u.id}`} onClick={() => setRole(u.id, "player")} className="px-2.5 py-1 rounded-lg text-xs bg-destructive/10 text-destructive border border-destructive/20 hover:bg-destructive/20 flex items-center gap-1 font-bold transition">
                    <UserX size={12} /> خفض
                  </button>
                ) : (
                  <button data-testid={`promote-${u.id}`} onClick={() => setRole(u.id, "admin")} className="px-2.5 py-1 rounded-lg text-xs bg-gold-500/10 text-gold-500 border border-gold-500/20 hover:bg-gold-500/20 flex items-center gap-1 font-bold transition">
                    <UserCheck size={12} /> منظم
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
        {!loadingUsers && !usersError && filtered.length === 0 && <div className="p-4 text-center text-white/40 text-xs">لا توجد نتائج مطابقة</div>}
      </div>
      {editTarget && (
        <EditUserModal target={editTarget} onClose={() => setEditTarget(null)} onSaved={load} />
      )}
    </section>
  );
}

function ClanEditor() {
  const [clans, setClans] = useState([]);
  const [archiveView, setArchiveView] = useState(false);
  const [clanLoading, setClanLoading] = useState(false);
  const [clanError, setClanError] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", tag: "", description: "" });
  const [transferTarget, setTransferTarget] = useState(null);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [suspendForm, setSuspendForm] = useState({ hours: 24, reason: "" });
  const [suspendBusy, setSuspendBusy] = useState(false);

  const load = useCallback(async () => {
    setClanLoading(true); setClanError(false);
    try {
      const { data } = await api.get(archiveView ? "/admin/clans/archived" : "/clans");
      setClans(data);
    } catch {
      setClans([]); setClanError(true);
    } finally { setClanLoading(false); }
  }, [archiveView]);
  useEffect(() => { load(); }, [load]);

  const startEdit = (c) => { setEditing(c); setForm({ name: c.name, tag: c.tag, description: c.description || "" }); };
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/clans/${editing.id}`, form);
      toast.success("تم تحديث الكلان");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const startTransfer = async (c) => {
    try {
      const { data } = await api.get(`/clans/${c.id}`);
      setTransferTarget(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const transferOwnership = async (memberId) => {
    if (!transferTarget) return;
    
    if (!confirm("نقل ملكية الكلان لهذا اللاعب؟")) return;
    try {
      await api.post(`/admin/clans/${transferTarget.id}/transfer/${memberId}`);
      toast.success("تم نقل ملكية الكلان");
      setTransferTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openSuspend = (clan) => {
    setSuspendTarget(clan);
    setSuspendForm({ hours: 24, reason: "" });
  };

  const submitSuspend = async (e) => {
    e.preventDefault();
    if (!suspendTarget) return;
    const hours = Number(suspendForm.hours || 0);
    if (!Number.isFinite(hours) || hours < 1) {
      return toast.error("مدة الإيقاف يجب أن تكون ساعة واحدة على الأقل");
    }
    setSuspendBusy(true);
    try {
      await api.post(`/admin/clans/${suspendTarget.id}/suspend`, {
        hours,
        reason: suspendForm.reason || "",
      });
      toast.success("تم إيقاف الكلان بنجاح");
      setSuspendTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSuspendBusy(false);
    }
  };

  return (
    <section>
      <h2 className="font-display font-black text-xl mb-3 flex items-center gap-2 text-white">
        <Shield className="text-gold-500" /> إدارة الكلانات
      </h2>
      <div className="flex gap-2 mb-3" role="group" aria-label="حالة الكلانات">
        <button type="button" aria-pressed={!archiveView} onClick={() => setArchiveView(false)} className="px-3 py-2 rounded-lg border border-white/20 aria-pressed:bg-white/15">الكلانات النشطة</button>
        <button type="button" aria-pressed={archiveView} onClick={() => setArchiveView(true)} className="px-3 py-2 rounded-lg border border-white/20 aria-pressed:bg-white/15">الكلانات المؤرشفة</button>
      </div>
      {clanLoading && <p role="status">جاري تحميل الكلانات…</p>}
      {clanError && <p role="alert">تعذر تحميل الكلانات. <button type="button" onClick={load}>إعادة المحاولة</button></p>}
      <div className="bg-[#111113]/90 border border-white/10 rounded-2xl divide-y divide-white/5 max-h-[400px] overflow-y-auto shadow-xl">
        {!clanLoading && !clanError && clans.map((c) => (
          <div key={c.id} className="p-3.5 flex items-center gap-3" data-testid={`admin-clan-${c.id}`}>
            <div className="h-10 w-10 rounded-xl bg-gold-500/10 border border-gold-500/20 grid place-items-center text-gold-500 shrink-0"><Shield size={18} /></div>
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate text-white text-sm">{c.name} <span className="text-xs text-gold-500 font-mono">[{c.tag}]</span></div>
              <div className="text-[11px] text-white/40 truncate">{c.points} نقطة • {c.member_ids?.length || 0} لاعب</div>
              {c.suspended_until && (
                <div className="text-[10px] text-rose-400 font-bold truncate">موقوف حتى {new Date(c.suspended_until).toLocaleString("ar")}</div>
              )}
            </div>
            {c.archived && <span className="text-xs text-white/60">مؤرشف • {c.archived_at ? new Date(c.archived_at).toLocaleDateString("ar") : ""}<br />{c.rank_points ?? 0} RR • {c.wins ?? 0} فوز • {c.losses ?? 0} خسارة</span>}
            {!c.archived && <>
            <button data-testid={`transfer-clan-${c.id}`} onClick={() => startTransfer(c)} className="px-2.5 py-1 rounded-lg text-xs bg-gold-500/10 text-gold-500 border border-gold-500/20 hover:bg-gold-500/20 flex items-center gap-1 font-bold transition">
              <Crown size={12} /> نقل الملكية
            </button>
            <button data-testid={`edit-clan-${c.id}`} onClick={() => startEdit(c)} className="px-2.5 py-1 rounded-lg text-xs bg-white/5 border border-white/10 hover:bg-white/10 text-white flex items-center gap-1 font-bold transition">
              <Edit3 size={12} /> تعديل
            </button>
            <button data-testid={`suspend-clan-${c.id}`} onClick={() => openSuspend(c)} className="px-2.5 py-1 rounded-lg text-xs bg-destructive/10 text-destructive border border-destructive/20 hover:bg-destructive/20 flex items-center gap-1 font-bold transition">
              <Power size={12} /> إيقاف الكلان
            </button>
            </>}
          </div>
        ))}
        {!clanLoading && !clanError && clans.length === 0 && <div className="p-4 text-center text-white/40 text-xs">لا توجد كلانات</div>}
      </div>
      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 backdrop-blur-md p-4">
          <form onSubmit={save} className="bg-[#141417] border border-white/10 rounded-2xl p-6 w-full max-w-md space-y-3.5 shadow-2xl" data-testid="edit-clan-form">
            <div className="flex items-center justify-between pb-2 border-b border-white/10">
              <h2 className="font-display font-black text-lg text-white">تعديل {editing.name}</h2>
              <button type="button" onClick={() => setEditing(null)} className="p-1 rounded-lg hover:bg-white/5 text-white/50 hover:text-white transition"><X size={16} /></button>
            </div>
            <input data-testid="edit-clan-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="الاسم" className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 outline-none text-sm text-white" />
            <input data-testid="edit-clan-tag" value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} placeholder="التاج" className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 outline-none text-sm text-white font-mono" />
            <textarea data-testid="edit-clan-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="الوصف" rows={2} className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 outline-none text-sm text-white resize-none" />
            <button data-testid="edit-clan-submit" type="submit" className="w-full py-2.5 rounded-xl bg-gold-500 text-black font-black hover:bg-gold-400 text-xs shadow-md transition">حفظ التغييرات</button>
          </form>
        </div>
      )}
      {transferTarget && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 backdrop-blur-md p-4">
          <div className="bg-[#141417] border border-white/10 rounded-2xl p-6 w-full max-w-md space-y-3.5 shadow-2xl" data-testid="transfer-modal">
            <div className="flex items-center justify-between pb-2 border-b border-white/10">
              <h2 className="font-display font-black text-lg text-white flex items-center gap-2">
                <Crown size={18} className="text-gold-500" /> نقل ملكية {transferTarget.name}
              </h2>
              <button type="button" onClick={() => setTransferTarget(null)} className="p-1 rounded-lg hover:bg-white/5 text-white/50 hover:text-white transition"><X size={16} /></button>
            </div>
            <p className="text-xs text-white/50">اختر اللاعب الذي ستنقل إليه القيادة. يجب أن يكون عضواً في الكلان.</p>
            <div className="max-h-[300px] overflow-y-auto divide-y divide-white/5">
              {(transferTarget.members || []).filter((m) => m.id !== transferTarget.leader_id).map((m) => (
                <button
                  key={m.id}
                  data-testid={`transfer-to-${m.id}`}
                  onClick={() => transferOwnership(m.id)}
                  className="w-full p-3 flex items-center gap-3 hover:bg-gold-500/10 text-right rounded-xl transition"
                >
                  <div className="h-8 w-8 rounded-lg bg-gold-500/10 border border-gold-500/20 grid place-items-center text-gold-500 font-display font-black text-xs">{m.username[0]?.toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-xs text-white">{m.username}</div>
                    <div className="text-[10px] text-white/40">{m.points || 0} نقطة</div>
                  </div>
                  <Crown size={14} className="text-gold-500 shrink-0" />
                </button>
              ))}
              {(transferTarget.members || []).filter((m) => m.id !== transferTarget.leader_id).length === 0 && (
                <div className="p-4 text-center text-white/40 text-xs">لا يوجد أعضاء آخرون</div>
              )}
            </div>
          </div>
        </div>
      )}
      {suspendTarget && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 backdrop-blur-md p-4">
          <form onSubmit={submitSuspend} className="bg-[#141417] border border-white/10 rounded-2xl p-6 w-full max-w-md space-y-3.5 shadow-2xl" data-testid="suspend-clan-form">
            <div className="flex items-center justify-between pb-2 border-b border-white/10">
              <h2 className="font-display font-black text-lg text-destructive">إيقاف الكلان: {suspendTarget.name}</h2>
              <button type="button" onClick={() => setSuspendTarget(null)} className="p-1 rounded-lg hover:bg-white/5 text-white/50 hover:text-white transition"><X size={16} /></button>
            </div>
            <div>
              <label className="text-xs font-bold text-white/70 mb-1 block">المدة (كم ساعة)</label>
              <input
                data-testid="suspend-hours"
                type="number"
                min={1}
                value={suspendForm.hours}
                onChange={(e) => setSuspendForm((p) => ({ ...p, hours: e.target.value }))}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 outline-none text-sm text-white"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-white/70 mb-1 block">سبب الإيقاف (اختياري)</label>
              <textarea
                data-testid="suspend-reason"
                value={suspendForm.reason}
                onChange={(e) => setSuspendForm((p) => ({ ...p, reason: e.target.value }))}
                rows={3}
                placeholder="مثال: مخالفات متكررة / سلوك غير رياضي"
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 outline-none text-sm text-white resize-none"
              />
            </div>
            <div className="flex items-center justify-end gap-2.5 pt-2">
              <button type="button" onClick={() => setSuspendTarget(null)} className="px-4 py-2 rounded-xl text-xs text-white/70 hover:bg-white/5 font-bold transition">إلغاء</button>
              <button data-testid="suspend-submit" type="submit" disabled={suspendBusy} className="px-5 py-2 rounded-xl bg-destructive text-white text-xs font-black hover:bg-destructive/90 disabled:opacity-50 transition shadow-md">
                {suspendBusy ? "جاري الإيقاف..." : "تأكيد الإيقاف"}
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}

function PasswordResetsPanel() {
  const [resets, setResets] = useState([]);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/password-resets");
      setResets(data);
    } catch (err) {
      // expected for non-staff
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const complete = async (rid) => {
    try {
      await api.post(`/admin/password-resets/${rid}/complete`);
      toast.success("تم تعليم الطلب كمكتمل");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <section data-testid="password-resets-section">
      <h2 className="font-display font-black text-xl mb-3 flex items-center gap-2 text-white">
        <KeyRound className="text-gold-500" /> طلبات استعادة كلمة المرور
      </h2>
      <div className="bg-[#111113]/90 border border-white/10 rounded-2xl divide-y divide-white/5 shadow-xl">
        {resets.length === 0 && (
          <div className="p-4 text-center text-white/40 text-xs">لا توجد طلبات معلّقة</div>
        )}
        {resets.map((r) => (
          <div key={r.id} className="p-3.5 flex items-center gap-3" data-testid={`reset-${r.id}`}>
            <Mail size={16} className="text-gold-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-xs text-white">{r.username} <span className="text-white/40">• {r.email}</span></div>
              <div className="text-[10px] text-white/40 mt-0.5">
                Token: <span className="text-gold-500 font-mono">{r.token}</span> • {new Date(r.created_at).toLocaleString("ar")}
              </div>
            </div>
            <button onClick={() => complete(r.id)} className="px-3 py-1.5 rounded-lg bg-gold-500/10 border border-gold-500/20 text-gold-500 text-xs font-bold hover:bg-gold-500/20 transition">
              تم الإرسال
            </button>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-white/40 mt-2">
        ⚠️ ميزة الإيميل غير مُفعّلة بعد. أرسل الرابط يدويًا للاعب أو وفّر API key لخدمة Resend/SendGrid.
      </p>
    </section>
  );
}

function LeaguesManager() {
  const [leagues, setLeagues] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", game: "Call of Duty", rules: "", description: "", rules_image: "", super_rivals_enabled: false });
  const [busy, setBusy] = useState(false);
  const [completingLeagueId, setCompletingLeagueId] = useState("");
  const [recentSuperRivals, setRecentSuperRivals] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/leagues/active");
      setLeagues(data);
    } catch (err) {
      // benign
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const reset = () => {
    setForm({ name: "", game: "Call of Duty", rules: "", description: "", rules_image: "", super_rivals_enabled: false });
    setCreating(false);
    setEditing(null);
  };

  const onImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3_000_000) return toast.error("الصورة كبيرة (الحد 3MB)");
    const fr = new FileReader();
    fr.onload = () => setForm((p) => ({ ...p, rules_image: fr.result }));
    fr.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.game) return toast.error("الاسم واللعبة إلزاميان");
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/leagues/${editing}`, form);
        toast.success("تم تحديث الدوري");
      } else {
        await api.post("/leagues/custom", form);
        toast.success("تم إنشاء الدوري");
      }
      reset();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const finishLeague = async (lid) => {
    const league = leagues.find((x) => x.id === lid);
    const superMsg = league?.super_rivals_enabled
      ? "سيتم إنشاء بطولة السوبر رايفلز تلقائياً من أفضل 4 كلانات."
      : "نظام السوبر رايفلز غير مفعل لهذا الدوري.";
  
      if (!confirm(`تأكيد اكتمال الدوري الآن؟ ${superMsg}`)) return;

    setCompletingLeagueId(lid);
    try {
      let result;
      try {
        const { data } = await api.patch(`/leagues/${lid}/status`, { status: "completed" });
        result = data;
      } catch {
        const { data } = await api.post(`/leagues/${lid}/complete`);
        result = data;
      }

      const tid = result?.super_rivals_tournament_id;
      if (tid) {
        setRecentSuperRivals((prev) => {
          const next = [
            {
              league_id: lid,
              tournament_id: tid,
              qualified_clan_ids: result?.qualified_clan_ids || [],
              created_at: new Date().toISOString(),
            },
            ...prev.filter((x) => x.league_id !== lid),
          ];
          return next.slice(0, 6);
        });
        toast.success("تم إنهاء الدوري وإنشاء بطولة السوبر رايفلز بنجاح", {
          description: "يمكنك فتح شجرة البطولة مباشرة من لوحة الإدارة.",
        });
      } else {
        toast.success("تم إنهاء الدوري");
      }
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCompletingLeagueId("");
    }
  };

  const startEdit = (lg) => {
    setEditing(lg.id);
    setForm({
      name: lg.name, game: lg.game,
      rules: lg.rules || "", description: lg.description || "",
      rules_image: lg.rules_image || "",
      super_rivals_enabled: !!lg.super_rivals_enabled,
    });
    setCreating(true);
  };

  return (
    <section data-testid="leagues-manager">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-display font-black text-xl flex items-center gap-2 text-white">
          <Gamepad2 className="text-gold-500" /> إدارة الدوريات المتعددة
        </h2>
        {!creating && (
          <button
            data-testid="open-league-form"
            onClick={() => setCreating(true)}
            className="px-3.5 py-2 rounded-xl bg-gold-500 text-black text-xs font-black hover:bg-gold-400 flex items-center gap-1.5 transition shadow-md"
          >
            <Plus size={15} /> إنشاء دوري جديد
          </button>
        )}
      </div>

      {creating && (
        <form onSubmit={submit} className="bg-[#141417] border border-white/10 rounded-2xl p-5 space-y-3.5 mb-4 shadow-xl" data-testid="league-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-white/70 mb-1 block">اسم الدوري</label>
              <input
                data-testid="league-name"
                required minLength={2} maxLength={80}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="دوري الأبطال - الموسم الأول"
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 outline-none text-sm text-white focus:border-gold-500/50"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-white/70 mb-1 block">اللعبة</label>
              <input
                data-testid="league-game"
                required minLength={2} maxLength={40}
                value={form.game}
                onChange={(e) => setForm({ ...form, game: e.target.value })}
                placeholder="Call of Duty MW3"
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 outline-none text-sm text-white focus:border-gold-500/50"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-white/70 mb-1 block">الوصف</label>
            <input
              data-testid="league-description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="بطولة خاصة بـ Plus، 8 كلانات، خروج المغلوب"
              className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 outline-none text-sm text-white"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-white/70 mb-1 block">قوانين الدوري (نص)</label>
            <textarea
              data-testid="league-rules"
              value={form.rules}
              onChange={(e) => setForm({ ...form, rules: e.target.value })}
              placeholder="مثال: BO3 - 5v5 - الخريطة الممنوعة Nuketown - الأسلحة المسموحة..."
              rows={4}
              className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 outline-none text-sm text-white resize-none"
            />
          </div>
          <label className="flex items-center gap-3 rounded-xl border border-gold-500/25 bg-gold-500/5 px-3 py-2.5 cursor-pointer hover:border-gold-500/45 transition">
            <input
              type="checkbox"
              checked={!!form.super_rivals_enabled}
              onChange={(e) => setForm({ ...form, super_rivals_enabled: e.target.checked })}
              className="h-4 w-4 accent-gold-500"
            />
            <span className="text-xs font-bold text-white/90">تفعيل نظام سوبر رايفلز لأول 4 مراكز</span>
          </label>
          <div>
            <label className="text-xs font-bold text-white/70 mb-1 block">صورة القوانين (اختياري — ≤3MB)</label>
            <div className="flex items-start gap-3 flex-wrap">
              <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-black/40 border border-white/10 hover:border-gold-500/40 text-xs text-white font-bold transition">
                <ImageIcon size={14} />
                <span>اختر صورة</span>
                <input data-testid="league-image-input" type="file" accept="image/*" onChange={onImage} className="hidden" />
              </label>
              <input
                data-testid="league-image-url"
                value={form.rules_image.startsWith("http") ? form.rules_image : ""}
                onChange={(e) => setForm({ ...form, rules_image: e.target.value })}
                placeholder="أو ألصق رابط صورة (https://...)"
                className="flex-1 min-w-[200px] bg-black/40 border border-white/10 rounded-xl px-3 py-2 outline-none text-sm text-white"
              />
              {form.rules_image && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, rules_image: "" })}
                  className="px-2.5 py-1.5 text-xs text-destructive hover:bg-destructive/10 rounded-lg transition"
                >
                  حذف الصورة
                </button>
              )}
            </div>
            {form.rules_image && (
              <img src={form.rules_image} alt="rules" className="mt-3 rounded-xl max-h-44 border border-white/10" />
            )}
          </div>
          <div className="flex items-center gap-2 justify-end pt-2">
            <button type="button" onClick={reset} className="px-3.5 py-2 rounded-xl text-xs font-bold text-white/70 hover:bg-white/5 transition">إلغاء</button>
            <button data-testid="league-submit" type="submit" disabled={busy} className="px-5 py-2 rounded-xl bg-gold-500 text-black text-xs font-black hover:bg-gold-400 disabled:opacity-50 shadow-md transition">
              {busy ? "جاري الحفظ..." : editing ? "حفظ التغييرات" : "إنشاء الدوري"}
            </button>
          </div>
        </form>
      )}

      {recentSuperRivals.length > 0 && (
        <div className="mb-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4" data-testid="super-rivals-generated-list">
          <div className="text-xs font-bold text-emerald-300 mb-2">تم إنشاء بطولات السوبر رايفلز</div>
          <div className="space-y-2">
            {recentSuperRivals.map((item) => (
              <div key={`${item.league_id}-${item.tournament_id}`} className="flex items-center justify-between gap-2 text-xs">
                <div className="text-white/80">
                  دوري #{item.league_id.slice(0, 8)} • المتأهلون: {item.qualified_clan_ids?.length || 0}
                </div>
                <Link
                  to={`/tournaments/${item.tournament_id}`}
                  className="px-2.5 py-1 rounded-lg bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 font-bold transition"
                >
                  فتح شجرة السوبر رايفلز
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-[#111113]/90 border border-white/10 rounded-2xl divide-y divide-white/5 shadow-xl">
        {leagues.length === 0 && (
          <div className="p-6 text-center text-white/40 text-xs">لا توجد دوريات نشطة حالياً</div>
        )}
        {leagues.map((lg) => (
          <div key={lg.id} data-testid={`league-row-${lg.id}`} className="p-3.5 flex items-center gap-3 flex-wrap">
            <div className="h-10 w-10 rounded-xl bg-gold-500/10 border border-gold-500/20 grid place-items-center text-gold-500 shrink-0">
              <Trophy size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-white text-sm truncate">{lg.name}</div>
              <div className="text-[11px] text-white/40">
                {lg.game} • {lg.is_custom ? "مخصص" : "شهري"}
                {lg.rules_image && <span className="text-gold-500"> • صورة قوانين ✓</span>}
                {lg.super_rivals_enabled && <span className="text-gold-400"> • سوبر رايفلز: مفعل</span>}
              </div>
            </div>
            <button data-testid={`edit-league-${lg.id}`} onClick={() => startEdit(lg)} className="px-2.5 py-1 rounded-lg text-xs bg-white/5 border border-white/10 hover:bg-white/10 text-white font-bold transition flex items-center gap-1">
              <Edit3 size={12} /> تعديل
            </button>
            <button
              data-testid={`finish-league-${lg.id}`}
              onClick={() => finishLeague(lg.id)}
              disabled={completingLeagueId === lg.id}
              className="px-2.5 py-1 rounded-lg text-xs bg-gold-500/10 text-gold-500 border border-gold-500/20 hover:bg-gold-500/20 font-bold disabled:opacity-50 transition"
            >
              {completingLeagueId === lg.id ? "جاري الإنهاء..." : "تأكيد اكتمال الدوري"}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

function OwnerPlusGrants() {
  const { user } = useAuth();
  const [userQuery, setUserQuery] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [clanQuery, setClanQuery] = useState("");
  const [clanResults, setClanResults] = useState([]);
  const [days, setDays] = useState(30);
  
  // 🟡 حالة إدارة نقاط الـ RIV للمستخدمين



  useEffect(() => {
    if (user?.role === "owner") {
      api.get("/clans").then((r) => setClanResults(r.data.slice(0, 60))).catch(() => {});
    }
  }, [user]);

  const searchUser = async (q) => {
    setUserQuery(q);
    if (q.length < 2) return setUserResults([]);
    try {
      const { data } = await api.get("/users/search", { params: { q } });
      setUserResults((Array.isArray(data) ? data : data.items || []).slice(0, 6));
    } catch {
      setUserResults([]);
    }
  };

  const searchClan = async (q) => {
    setClanQuery(q);
    try {
      const query = (q || "").trim();
      const { data } = await api.get("/clans", query ? { params: { q: query } } : undefined);
      setClanResults((data || []).slice(0, query ? 25 : 60));
    } catch {
      setClanResults([]);
    }
  };

  const grantUser = async (uid, action) => {
    try {
      await api.post(`/admin/users/${uid}/personal-plus`, { action, days });
      toast.success(action === "grant" ? "تم تفعيل Personal Plus" : "تم إلغاء Personal Plus");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const grantClan = async (cid, action) => {
    try {
      await api.post(`/admin/clans/${cid}/plus`, { action, days });
      toast.success(action === "grant" ? "تم تفعيل Clan Plus" : "تم إلغاء Clan Plus");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  // 🟡 دالة منح/سلب نقاط RIV يدوياً لأي مستخدم
  if (user?.role !== "owner") return null;

  return (
    <section data-testid="owner-plus-block" className="bg-gold-500/5 border border-gold-500/30 rounded-2xl p-6 shadow-xl space-y-6">
      <div>
        <h2 className="font-display font-black text-xl text-gold-500 mb-1 flex items-center gap-2">
          <Zap className="text-gold-500" /> منح Plus يدوياً (المالك فقط)
        </h2>
        <p className="text-xs text-white/50">امنح اشتراكات Plus لأي حساب أو كلان. إدارة نقاط RIV في أعلى لوحة الإدارة.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <div className="text-xs uppercase font-bold tracking-widest text-gold-500 mb-2">Personal Plus لمستخدم</div>
          <div className="flex items-center gap-2 mb-2">
            <input
              data-testid="plus-days"
              type="number" min={1} max={3650}
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="w-20 bg-black/40 border border-white/10 rounded-xl px-2.5 py-1 text-sm text-white"
            />
            <span className="text-xs text-white/50 font-bold">يوم</span>
          </div>
          <div className="relative">
            <Search size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30" />
            <input
              data-testid="plus-user-search"
              value={userQuery}
              onChange={(e) => searchUser(e.target.value)}
              placeholder="ابحث باسم أو بريد..."
              className="w-full bg-black/40 border border-white/10 rounded-xl pr-9 pl-3 py-2 outline-none text-sm text-white"
            />
          </div>
          <div className="mt-2 space-y-1">
            {userResults.map((u) => (
              <div key={u.id} className="flex items-center gap-2 bg-black/30 border border-white/5 rounded-xl p-2 text-xs text-white" data-testid={`plus-user-${u.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{u.username}</div>
                  <div className="text-[10px] text-white/40 truncate">{u.email}</div>
                </div>
                {u.is_personal_plus && <span className="text-[10px] uppercase font-bold bg-gold-500 text-black px-1.5 py-0.5 rounded">Plus</span>}
                <button data-testid={`grant-user-${u.id}`} onClick={() => grantUser(u.id, "grant")} className="px-2 py-1 rounded-lg bg-gold-500/20 text-gold-400 font-bold hover:bg-gold-500/30 text-xs">منح</button>
                <button data-testid={`revoke-user-${u.id}`} onClick={() => grantUser(u.id, "revoke")} className="px-2 py-1 rounded-lg bg-destructive/20 text-destructive font-bold hover:bg-destructive/30 text-xs">إلغاء</button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="text-xs uppercase font-bold tracking-widest text-emerald-400 mb-2">Clan Plus لكلان</div>
          <div className="relative mb-2">
            <Search size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30" />
            <input
              data-testid="plus-clan-search"
              value={clanQuery}
              onChange={(e) => searchClan(e.target.value)}
              placeholder="ابحث باسم الكلان أو ID..."
              className="w-full bg-black/40 border border-white/10 rounded-xl pr-9 pl-3 py-2 outline-none text-sm text-white"
            />
          </div>
          <div className="max-h-[260px] overflow-y-auto space-y-1">
            {clanResults.map((c) => (
              <div key={c.id} className="flex items-center gap-2 bg-black/30 border border-white/5 rounded-xl p-2 text-xs text-white" data-testid={`plus-clan-${c.id}`}>
                <Shield size={14} className="text-emerald-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{c.name} <span className="text-xs text-emerald-400 font-mono">[{c.tag}]</span></div>
                  <div className="text-[10px] text-white/40 truncate">ID: {c.id}</div>
                </div>
                {c.is_plus && <span className="text-[10px] uppercase font-bold bg-emerald-500 text-black px-1.5 py-0.5 rounded">Plus</span>}
                <button data-testid={`grant-clan-${c.id}`} onClick={() => grantClan(c.id, "grant")} className="px-2 py-1 rounded-lg bg-emerald-500/20 text-emerald-400 font-bold hover:bg-emerald-500/30 text-xs">منح</button>
                <button data-testid={`revoke-clan-${c.id}`} onClick={() => grantClan(c.id, "revoke")} className="px-2 py-1 rounded-lg bg-destructive/20 text-destructive font-bold hover:bg-destructive/30 text-xs">إلغاء</button>
              </div>
            ))}
            {clanResults.length === 0 && (
              <div className="text-xs text-white/40 py-3 text-center">لا توجد نتائج كلانات</div>
            )}
          </div>
        </div>
      </div>

    </section>
  );
}

function SanadAnalyticsPanel() {
  const [data, setData] = useState({
    total_questions: 0,
    questions_today: 0,
    top_users: [],
    top_matches: [],
    latest_questions: [],
  });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/sanad-analytics");
      setData({
        total_questions: Number(data?.total_questions || 0),
        questions_today: Number(data?.questions_today || 0),
        top_users: Array.isArray(data?.top_users) ? data.top_users : [],
        top_matches: Array.isArray(data?.top_matches) ? data.top_matches : [],
        latest_questions: Array.isArray(data?.latest_questions) ? data.latest_questions : [],
      });
    } catch {
      setData({
        total_questions: 0,
        questions_today: 0,
        top_users: [],
        top_matches: [],
        latest_questions: [],
      });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section className="bg-[#111113]/90 border border-white/10 rounded-2xl p-5 shadow-xl">
      <h2 className="font-display font-black text-xl mb-4 flex items-center gap-2 text-white">
        <Bot className="text-emerald-400" /> تحليلات سند الذكي
      </h2>

      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <div className="rounded-xl border border-white/10 bg-black/40 p-3">
          <div className="text-[10px] uppercase font-bold tracking-widest text-white/45">إجمالي الأسئلة</div>
          <div className="text-2xl font-black text-emerald-400">{data.total_questions}</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-black/40 p-3">
          <div className="text-[10px] uppercase font-bold tracking-widest text-white/45">أسئلة اليوم</div>
          <div className="text-2xl font-black text-emerald-400">{data.questions_today}</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-white/10 bg-black/40 p-3">
          <div className="text-xs font-bold text-white/70 mb-2">أكثر اللاعبين سؤالاً</div>
          <div className="space-y-1">
            {data.top_users.length === 0 && <div className="text-xs text-white/40">لا بيانات بعد</div>}
            {data.top_users.map((u) => (
              <div key={u.user_id} className="flex items-center justify-between text-xs text-white">
                <span className="truncate">{u.username || u.user_id}</span>
                <span className="text-emerald-400 font-bold">{u.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-white/10 bg-black/40 p-3">
          <div className="text-xs font-bold text-white/70 mb-2">أكثر المباريات تفاعلاً مع سند</div>
          <div className="space-y-1">
            {data.top_matches.length === 0 && <div className="text-xs text-white/40">لا بيانات بعد</div>}
            {data.top_matches.map((m) => (
              <div key={m.match_id} className="flex items-center justify-between text-xs text-white">
                <span className="truncate font-mono">{m.match_id}</span>
                <span className="text-emerald-400 font-bold">{m.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-white/10 bg-black/40 p-3">
        <div className="text-xs font-bold text-white/70 mb-2">آخر الأسئلة التي طُرحت</div>
        <div className="space-y-2 max-h-56 overflow-y-auto">
          {data.latest_questions.length === 0 && <div className="text-xs text-white/40">لا أسئلة مسجلة</div>}
          {data.latest_questions.map((q, idx) => (
            <div key={`${q.created_at}-${idx}`} className="text-xs border-b border-white/5 pb-2">
              <div className="text-white/80 font-bold">{q.asked_by_username || "-"} • {q.match_id || "-"}</div>
              <div className="text-white/50 mt-0.5 line-clamp-2">{q.question || ""}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function GuardAlertsPanel() {
  const [alerts, setAlerts] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/guard/alerts");
      setAlerts(Array.isArray(data) ? data : []);
    } catch {
      setAlerts([]);
    }
  }, []);

  useEffect(() => {
    load();
    // End Task / crash alerts are generated by the server watchdog, so refresh
    // this panel frequently enough that admins see them almost immediately.
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, [load]);

  const cleanup = async (id) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.delete(`/admin/guard/alerts/${id}`);
      toast.success("تم حذف التنبيه");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const hwidBan = async (hwid) => {
    if (!hwid) return toast.error("لا يوجد HWID في هذا التنبيه");
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/admin/guard/hwid-ban", {
        hwid_hash: hwid,
        reason: "Manual admin action from Rivals Guard dashboard",
      });
      toast.success("تم تطبيق HWID Ban");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-[#111113]/90 border border-destructive/30 rounded-2xl p-5 shadow-xl">
      <h2 className="font-display font-black text-xl mb-4 flex items-center gap-2 text-destructive">
        <ShieldAlert size={20} /> Rivals Guard Red Alerts
      </h2>

      <div className="space-y-2 max-h-[420px] overflow-y-auto">
        {alerts.map((a) => {
          const isDisconnect = a.detection_type === "guard_disconnect";
          return (
            <div
              key={a.id}
              className={`rounded-xl border p-3.5 text-xs text-white ${
                isDisconnect
                  ? "border-amber-500/35 bg-amber-500/10"
                  : "border-destructive/30 bg-destructive/10"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-bold truncate">{a.title || "Rivals Guard Alert"}</div>
                <span className={`text-[10px] uppercase font-black px-2 py-0.5 rounded ${isDisconnect ? "bg-amber-500 text-black" : "bg-destructive text-white"}`}>
                  {isDisconnect ? "GUARD OFFLINE" : (a.severity || "high")}
                </span>
              </div>

              <div className="text-[11px] text-white/60 mt-1">
                Match: {a.match_id} • Player: {a.reporter_username || "-"}
              </div>
              <div className="text-[10px] text-white/45 mt-1 font-mono">
                Type: {a.detection_type || "unknown"}
              </div>

              {a.last_seen_at && (
                <div className="text-[11px] text-amber-200/80 mt-2">
                  آخر Heartbeat: {new Date(a.last_seen_at).toLocaleString("ar")}
                </div>
              )}
              {a.last_event && (
                <div className="text-[11px] text-white/60 mt-1">
                  آخر حدث مسجل: <span className="font-mono text-white/80">{a.last_event}</span>
                </div>
              )}

              <div className="text-xs text-white/80 mt-2 leading-relaxed">{a.description || "لا يوجد وصف"}</div>

              {a.screenshot_url && (
              <div className="mt-3 overflow-hidden rounded-xl border border-white/10 bg-black/40">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}${a.screenshot_url}`}
                  alt="RivalsGuard evidence screenshot"
                  className="w-full max-h-72 object-contain bg-black"
                  loading="lazy"
                />
              </div>
            )}
            <div className="mt-3 flex items-center gap-2 flex-wrap">
                {a.zip_url && (
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}${a.zip_url}`}
                    target="_blank"
                    rel="noreferrer"
                    className="px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-bold flex items-center gap-1 transition"
                  >
                    <Download size={13} /> Download Zip
                  </a>
                )}

                {a.hwid_hash && (
                  <button
                    onClick={() => hwidBan(a.hwid_hash || "")}
                    className="px-2.5 py-1.5 rounded-lg bg-amber-500/20 text-amber-300 border border-amber-500/30 hover:bg-amber-500/30 text-xs font-bold flex items-center gap-1 transition"
                  >
                    <Ban size={13} /> HWID Ban
                  </button>
                )}

                <button
                  onClick={() => cleanup(a.id)}
                  className="px-2.5 py-1.5 rounded-lg bg-destructive/20 text-destructive border border-destructive/30 hover:bg-destructive/30 text-xs font-bold flex items-center gap-1 transition"
                >
                  <Trash2 size={13} /> Cleanup
                </button>
              </div>
            </div>
          );
        })}
        {alerts.length === 0 && (
          <div className="text-xs text-white/45 text-center py-4">لا توجد تنبيهات Guard حالياً.</div>
        )}
      </div>
    </section>
  );
}

function MatchLogsPanel() {
  const [logs, setLogs] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/guard/match-logs");
      setLogs(Array.isArray(data) ? data : []);
    } catch {
      setLogs([]);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  const getTimeRemaining = (expiresAt) => {
    if (!expiresAt) return "—";
    const diff = new Date(expiresAt) - new Date();
    if (diff <= 0) return "منتهي الصلاحية";
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours} ساعة و ${mins} دقيقة`;
  };

  return (
    <section className="bg-[#111113]/90 border border-white/10 rounded-2xl p-5 shadow-xl">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-display font-black text-xl flex items-center gap-2 text-gold-500">
          <FolderArchive size={22} /> سجلات المباريات الكاملة (تُحذف تلقائياً بعد 24 ساعة)
        </h2>
        <span className="text-xs bg-gold-500/10 text-gold-500 px-2.5 py-1 rounded-full border border-gold-500/20 font-bold">
          إجمالي المتاح: {logs.length}
        </span>
      </div>

      <div className="space-y-2 max-h-[420px] overflow-y-auto">
        {logs.map((log) => (
          <div key={log.id} className="rounded-xl border border-white/10 bg-black/40 p-3.5 text-xs flex items-center justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="font-bold text-white flex items-center gap-2">
                <span>اللاعب: {log.username || "غير معروف"}</span>
                <span className="text-[11px] text-gold-500 font-mono">[{log.zip_size ? (log.zip_size / (1024 * 1024)).toFixed(2) : 0} MB]</span>
              </div>
              <div className="text-xs text-white/50 mt-1">
                المباراة: <Link to={`/matches/${log.match_id}`} className="text-emerald-400 hover:underline font-mono">{log.match_id}</Link>
              </div>
            </div>

            <div className="flex items-center gap-1.5 text-xs text-amber-300 bg-amber-500/10 px-2.5 py-1.5 rounded-xl border border-amber-500/20 font-bold">
              <Clock size={14} />
              <span>متبقي للحذف: {getTimeRemaining(log.expires_at)}</span>
            </div>

            <a
              href={`${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}${log.zip_url}`}
              target="_blank"
              rel="noreferrer"
              className="px-3 py-2 rounded-xl bg-gold-500 text-black font-black hover:bg-gold-400 text-xs flex items-center gap-1 transition shadow-md"
            >
              <Download size={14} /> تحميل سجل الجلسة (ZIP)
            </a>
          </div>
        ))}

        {logs.length === 0 && (
          <div className="text-xs text-white/40 text-center py-6">
            لا توجد سجلات مباريات مرفوعة حالياً. (تُرفع أوتوماتيكياً فور انتهاء أي مباراة).
          </div>
        )}
      </div>
    </section>
  );
}

function PlayerReportsAdmin() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/admin/player-reports").then(r => setRows(r.data || [])).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (r, status) => {
    if (status !== "reviewing" && r.reviewing_by && r.reviewing_by !== user?.id && !confirm(`هذا البلاغ قيد المراجعة بواسطة ${r.reviewing_user?.username || "أدمن آخر"}. هل تريد المتابعة؟`)) return;
    try {
      await api.put(`/admin/player-reports/${r.id}`, { status, admin_note: "" });
      toast.success("تم تحديث حالة البلاغ");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذر تحديث البلاغ");
    }
  };
  const remove = async (id) => {
    if (!confirm("حذف البلاغ نهائيًا؟")) return;
    try { await api.delete(`/admin/player-reports/${id}`); toast.success("تم حذف البلاغ"); await load(); }
    catch(e){ toast.error(e?.response?.data?.detail || "تعذر حذف البلاغ"); }
  };
  const active = rows.filter(r => !["action_taken","rejected","reviewed"].includes(r.status));
  const history = rows.filter(r => ["action_taken","rejected","reviewed"].includes(r.status));
  const statusLabel = s => ({new:"جديد",reviewing:"قيد المراجعة",action_taken:"تم اتخاذ إجراء",rejected:"مرفوض",reviewed:"تمت المراجعة"}[s] || s);
  const card = r => <div key={r.id} className="rounded-xl border border-white/10 p-3 text-xs space-y-2">
    <div className="font-bold text-white">المبلّغ عنه: {r.reported_user?.username || r.reported_user_id}</div>
    <div className="text-white/50">صاحب البلاغ (للإدارة فقط): {r.reporter_user?.username || r.reporter_user_id}</div>
    <div className="text-red-300">{r.reason}</div><div className="text-white/70 whitespace-pre-wrap">{r.message}</div>
    <div className="text-white/40">الحالة: {statusLabel(r.status || "new")}</div>
    {r.status === "reviewing" && <div className="text-amber-300">قيد المراجعة بواسطة: {r.reviewing_user?.username || "أدمن"}{r.reviewing_at ? ` • ${new Date(r.reviewing_at).toLocaleString("ar")}` : ""}</div>}
    {!history.includes(r) ? <div className="flex gap-2 flex-wrap"><button onClick={()=>setStatus(r,"reviewing")} className="px-2 py-1 border border-white/10 rounded">قيد المراجعة</button><button onClick={()=>setStatus(r,"action_taken")} className="px-2 py-1 border border-emerald-500/30 text-emerald-300 rounded">تم اتخاذ إجراء</button><button onClick={()=>setStatus(r,"rejected")} className="px-2 py-1 border border-red-500/30 text-red-300 rounded">مرفوض</button></div> : <button onClick={()=>remove(r.id)} className="px-2 py-1 border border-red-500/30 text-red-300 rounded flex items-center gap-1"><Trash2 size={12}/> حذف الآن</button>}
  </div>;
  return <section className="bg-[#111113]/90 border border-white/10 rounded-2xl p-5 space-y-5">
    <div><h2 className="font-black text-white">بلاغات اللاعبين النشطة</h2>{active.length === 0 ? <p className="text-xs text-white/40 mt-2">لا توجد بلاغات نشطة.</p> : <div className="space-y-3 mt-3">{active.map(card)}</div>}</div>
    <div className="border-t border-white/10 pt-4"><h3 className="font-bold text-white/70">سجل البلاغات المعالجة</h3><p className="text-[11px] text-white/35 mt-1">تُحذف البلاغات المعالجة تلقائيًا بعد 24 ساعة.</p>{history.length === 0 ? <p className="text-xs text-white/40 mt-2">لا يوجد سجل حاليًا.</p> : <div className="space-y-3 mt-3">{history.map(card)}</div>}</div>
  </section>;
}

function RankSystemAdmin() {
  const [data, setData] = useState({ ranks: [], settings: {} });
  const [busy, setBusy] = useState(false);
  const [clans, setClans] = useState([]);
  const [manualClan, setManualClan] = useState("");
  const [manualDelta, setManualDelta] = useState("");
  const [manualReason, setManualReason] = useState("اختبار نظام RR");
  const [finishPlacement, setFinishPlacement] = useState(false);
  const load = useCallback(() => Promise.all([api.get('/rank-system'), api.get('/clans')]).then(([r,c]) => {
    setData(r.data || { ranks: [], settings: {} });
    setClans(c.data || []);
  }), []);
  useEffect(() => { load(); }, [load]);

  const saveRank = async (r) => {
    const payload = { name: r.name, min_rr: Number(r.min_rr), logo: r.logo || "", top_limit: Number(r.top_limit || 0) };
    if (r.id) await api.put(`/admin/rank-system/ranks/${r.id}`, payload);
    else await api.post('/admin/rank-system/ranks', payload);
    toast.success('تم حفظ الرانك'); load();
  };
  const addRank = () => setData(d => ({ ...d, ranks: [...d.ranks, { id: null, name: 'Rank جديد', min_rr: 0, logo: '', top_limit: 0 }] }));
  const uploadRankLogo = async (r, file) => {
    if (!r.id || !file) return toast.error('احفظ الرانك أولاً ثم ارفع الشعار');
    const fd = new FormData(); fd.append('file', file);
    try { await api.post(`/admin/rank-system/ranks/${r.id}/logo/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success('تم رفع الشعار'); load(); }
    catch(e){ toast.error(e?.response?.data?.detail || 'تعذر رفع الشعار'); }
  };
  const restoreDefaults = async () => {
    if (!confirm('استرجاع الرتب الافتراضية Rookie / Pro / Elite / Master؟ سيتم حذف تعريفات الرتب الحالية فقط ولن يتم حذف RR الكلانات.')) return;
    await api.post('/admin/rank-system/restore-defaults'); toast.success('تم استرجاع الرتب الافتراضية'); load();
  };
  const adjustRR = async () => {
    if (!manualClan || !manualDelta || !manualReason.trim()) return toast.error('اختر كلان واكتب مقدار RR والسبب');
    try { const r = await api.post(`/admin/rank-system/clans/${manualClan}/adjust-rr`, { delta: Number(manualDelta), reason: manualReason.trim(), finish_placement: finishPlacement }); toast.success(`RR: ${r.data.before} → ${r.data.after}`); setManualDelta(''); load(); }
    catch(e){ toast.error(e?.response?.data?.detail || 'تعذر تعديل RR'); }
  };
  const deleteRank = async (r, i) => {
    if (!r.id) return setData(d => ({ ...d, ranks: d.ranks.filter((_, j) => j !== i) }));
    if (!confirm(`حذف الرانك ${r.name}؟`)) return;
    await api.delete(`/admin/rank-system/ranks/${r.id}`); toast.success('تم حذف الرانك'); load();
  };
  const saveSettings = async () => {
    const x = data.settings || {};
    await api.put('/admin/rank-system/settings', {
      placement_matches: Number(x.placement_matches || 5),
      base_change: Number(x.base_change || 20),
      min_change: Number(x.min_change || 10),
      max_change: Number(x.max_change || 35),
      mmr_k_factor: Number(x.mmr_k_factor || 32),
      new_clan_pressure_start_rr: Number(x.new_clan_pressure_start_rr ?? 1000),
      historical_season_weight: Number(x.historical_season_weight ?? 0.5),
      placement_best_drop_levels: Number(x.placement_best_drop_levels ?? 3),
      placement_worst_drop_levels: Number(x.placement_worst_drop_levels ?? 6)
    });
    toast.success('تم حفظ إعدادات RR'); load();
  };
  const setSetting = (key, value) => setData(d => ({ ...d, settings: { ...(d.settings || {}), [key]: value } }));
  const newSeason = async () => {
    if (!confirm('بدء موسم جديد؟ سيتم حفظ RR والرانك الحالي كـ Hidden MMR، وإعادة جميع الكلانات إلى Unranked وPlacement.')) return;
    setBusy(true); try { const r = await api.post('/admin/rank-system/new-season'); toast.success(`تم بدء الموسم الجديد لـ ${r.data.clans_reset} كلان`); load(); } finally { setBusy(false); }
  };
  return <section className="bg-[#111113]/90 border border-white/10 rounded-2xl p-5 space-y-5">
    <div className="flex justify-between gap-3 flex-wrap"><div><h2 className="font-black text-white">إدارة Rank / RR</h2><p className="text-xs text-white/40">RR مستقل عن الـLeaderboard. Hidden MMR والمتوسط التاريخي مخفيان عن اللاعبين. RR النهائي لكل مباراة بين 10 و35 حسب الإعدادات وقوة الخصم والتاريخ.</p></div><button disabled={busy} onClick={newSeason} className="px-3 py-2 rounded-xl bg-red-600 text-white text-xs font-bold">بدء موسم جديد</button></div>
    <div className="border border-white/10 rounded-xl p-4 space-y-3"><div className="font-bold text-white">إعدادات النظام</div><div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
      {[
        ['placement_matches','عدد مباريات Placement','1'],
        ['base_change','RR الأساسي','1'],
        ['min_change','أقل تغيير RR','1'],
        ['max_change','أقصى تغيير RR','1'],
        ['mmr_k_factor','سرعة تغير Hidden MMR','1'],
        ['new_clan_pressure_start_rr','بداية التشدد للكلان بدون تاريخ (RR)','1'],
        ['historical_season_weight','وزن الموسم الجديد في المتوسط التاريخي (0 إلى 1)','0.05'],
        ['placement_best_drop_levels','أفضل Placement: النزول عن المستوى التاريخي (مستويات)','1'],
        ['placement_worst_drop_levels','أسوأ Placement: النزول عن المستوى التاريخي (مستويات)','1']
      ].map(([k,label,step])=><label key={k} className="space-y-1"><span className="text-white/50">{label}</span><input type="number" step={step} value={data.settings?.[k] ?? ''} onChange={e=>setSetting(k,e.target.value)} className="w-full bg-black/30 rounded p-2 text-white"/></label>)}
    </div><button onClick={saveSettings} className="px-3 py-2 bg-gold-500 text-black rounded-lg font-bold text-xs">حفظ إعدادات RR</button></div>
    <div className="border border-white/10 rounded-xl p-4 space-y-3">
      <div className="font-bold text-white">تعديل RR يدويًا للاختبار</div>
      <div className="grid md:grid-cols-3 gap-2">
        <select value={manualClan} onChange={e=>setManualClan(e.target.value)} className="bg-black/30 rounded p-2 text-white"><option value="">اختر الكلان</option>{clans.map(c=><option key={c.id} value={c.id}>{c.name} — {c.rank_tier || 'Unranked'} — {c.rank_points ?? 100} RR</option>)}</select>
        <input type="number" value={manualDelta} onChange={e=>setManualDelta(e.target.value)} placeholder="مثال +500 أو -200" className="bg-black/30 rounded p-2 text-white"/>
        <input value={manualReason} onChange={e=>setManualReason(e.target.value)} placeholder="سبب التعديل" className="bg-black/30 rounded p-2 text-white"/>
      </div><label className="flex items-center gap-2 text-xs text-amber-200"><input type="checkbox" checked={finishPlacement} onChange={e=>setFinishPlacement(e.target.checked)} /> إنهاء Placement وإظهار الرانك فورًا (للاختبار)</label><button onClick={adjustRR} className="px-3 py-2 bg-gold-500 text-black rounded-lg font-bold text-xs">تطبيق تعديل RR</button>
      <p className="text-[11px] text-white/40">أثناء Placement يبقى RR مخفيًا للمستخدم حتى نهاية مباريات Placement، والتعديل اليدوي لا يغير Hidden MMR.</p>
    </div>
    <div className="flex items-center justify-between gap-2 flex-wrap"><div className="font-bold text-white">الرتب</div><div className="flex gap-2"><button onClick={restoreDefaults} className="px-3 py-2 rounded-lg border border-white/20 text-white/70 text-xs font-bold">استرجاع الرتب الافتراضية</button><button onClick={addRank} className="px-3 py-2 rounded-lg border border-gold-500/40 text-gold-300 text-xs font-bold">+ إضافة Rank</button></div></div>
    <div className="grid md:grid-cols-2 gap-3">{data.ranks.map((r,i)=><div key={r.id||`new-${i}`} className="border border-white/10 rounded-xl p-3 grid grid-cols-2 gap-2">
      <input value={r.name} placeholder="اسم الرانك" onChange={e=>setData(d=>({...d,ranks:d.ranks.map((x,j)=>j===i?{...x,name:e.target.value}:x)}))} className="bg-black/30 rounded p-2 text-white"/>
      <input type="number" value={r.min_rr} placeholder="Minimum RR" onChange={e=>setData(d=>({...d,ranks:d.ranks.map((x,j)=>j===i?{...x,min_rr:e.target.value}:x)}))} className="bg-black/30 rounded p-2 text-white"/>
      <input value={r.logo||''} placeholder="رابط مباشر لشعار الرانك (Discord/CDN أو أي URL)" onChange={e=>setData(d=>({...d,ranks:d.ranks.map((x,j)=>j===i?{...x,logo:e.target.value}:x)}))} className="bg-black/30 rounded p-2 col-span-2 text-white"/>
      <label className="col-span-2 text-xs text-white/50 border border-white/10 rounded p-2">أو ارفع صورة للشعار <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={e=>uploadRankLogo(r,e.target.files?.[0])} className="block mt-1 text-white/70"/></label>
      <input type="number" min="0" value={r.top_limit ?? 0} placeholder="Top limit — ضع 0 للرتب العادية و10 لأعلى Rank" onChange={e=>setData(d=>({...d,ranks:d.ranks.map((x,j)=>j===i?{...x,top_limit:e.target.value}:x)}))} className="bg-black/30 rounded p-2 col-span-2 text-white"/>
      <button onClick={()=>saveRank(data.ranks[i])} className="bg-gold-500 text-black rounded p-2 font-bold">حفظ</button><button onClick={()=>deleteRank(r,i)} className="border border-red-500/30 text-red-300 rounded p-2 font-bold">حذف</button>
    </div>)}</div>
  </section>;
}

function AdminMatchTools() {
  const { user } = useAuth();
  const [live, setLive] = useState([]);
  const [history, setHistory] = useState([]);
  const [clans, setClans] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [overrideMatchTarget, setOverrideMatchTarget] = useState(null);

  const loadData = useCallback(() => {
    if (!user || (user.role !== "admin" && user.role !== "owner")) return;
    api.get("/matches/live")
      .then((r) => {
        setLive(r.data || []);
        setDisputes((r.data || []).filter((m) => (m.maps || []).some((mp) => mp.disputed)));
      })
      .catch(() => {
        setLive([]);
        setDisputes([]);
      });
    api.get("/matches/history").then((r) => setHistory(r.data || [])).catch(() => setHistory([]));
    api.get("/clans").then((r) => setClans(r.data || [])).catch(() => setClans([]));
    api.get("/tournaments").then((r) => setTournaments(r.data || [])).catch(() => setTournaments([]));
  }, [user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return <div className="space-y-6">
    <AdminLinks items={[["/matches","كل المباريات","افتح سجل المواجهات وتفاصيل المباراة وأدوات المنظم." ]]}/>
    <section className="bg-[#111113]/90 border border-white/10 rounded-2xl p-5"><h2 className="text-lg font-bold mb-3">المباريات النشطة ({live.length})</h2>
      <div className="space-y-2">{live.map(m=><Link key={m.id} to={`/matches/${m.id}`} className="flex flex-wrap justify-between gap-2 rounded-xl border border-white/10 p-3 text-xs"><span>{m.clan_a?.name||m.clan_a?.tag} × {m.clan_b?.name||m.clan_b?.tag}</span><span className="text-gold-400">تفاصيل المباراة وقرارات المنظم ←</span></Link>)}{!live.length&&<p className="text-xs text-white/40">لا توجد مباريات نشطة معروضة.</p>}</div>
    </section>
      {disputes.length > 0 && (
        <section>
          <h2 className="font-display font-black text-xl mb-3 text-destructive flex items-center gap-2">
            <AlertCircle /> نزاعات تنتظر قرارك ({disputes.length})
          </h2>
          <div className="bg-[#111113]/90 border border-destructive/30 rounded-2xl divide-y divide-white/5 shadow-xl">
            {disputes.map((m) => (
              <Link key={m.id} to={`/matches/${m.id}`} className="p-4 flex items-center gap-4 hover:bg-destructive/10 transition">
                <span className="live-dot" />
                <div className="flex-1 flex items-center gap-3 text-white text-sm">
                  <span className="font-bold">{m.clan_a?.tag}</span>
                  <span className="text-white/40">vs</span>
                  <span className="font-bold">{m.clan_b?.tag}</span>
                </div>
                <span className="text-xs text-destructive font-bold">حلّ النزاع ←</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ⚖️ قسم المباريات المنتهية خلال 24 ساعة للتحكم بسلب/تعديل النتيجة */}
      <section className="bg-[#111113]/90 border border-white/10 rounded-2xl p-5 shadow-xl">
        <h2 className="font-display font-black text-xl mb-3 flex items-center gap-2 text-gold-500">
          <RefreshCw size={20} /> المباريات المنتهية مؤخراً (إمكانية التعديل وسلب النقاط خلال 24 ساعة)
        </h2>
        <div className="space-y-2 max-h-[350px] overflow-y-auto">
          {history.map((m) => (
            <div key={m.id} className="p-3.5 bg-black/40 border border-white/10 rounded-xl flex items-center justify-between gap-3 text-xs flex-wrap">
              <div>
                <div className="font-bold text-white flex items-center gap-2 text-sm">
                  <span>{m.clan_a?.name} ({m.score_a || 0})</span>
                  <span className="text-white/30">vs</span>
                  <span>({m.score_b || 0}) {m.clan_b?.name}</span>
                </div>
                <div className="text-[11px] text-gold-500 font-bold mt-0.5">
                  الفائز: {m.winner_clan_id === m.clan_a_id ? m.clan_a?.name : m.clan_b?.name}
                </div>
              </div>

              <button
                onClick={() => setOverrideMatchTarget(m)}
                className="px-3 py-1.5 rounded-lg bg-gold-500/10 text-gold-500 border border-gold-500/30 hover:bg-gold-500/20 text-xs font-bold transition flex items-center gap-1 shadow-sm"
              >
                <Edit3 size={13} /> تعديل النتيجة وسلب النقاط
              </button>
            </div>
          ))}
          {history.length === 0 && (
            <div className="text-xs text-white/40 text-center py-6">لا توجد مباريات منتهية خلال الـ 24 ساعة الماضية.</div>
          )}
        </div>
      </section>

      {/* مودال التعديل وسلب النقاط للمباراة */}
      {overrideMatchTarget && (
        <AdminOverrideMatchModal
          match={overrideMatchTarget}
          onClose={() => setOverrideMatchTarget(null)}
          onSaved={loadData}
        />
      )}
  </div>;
}

export default function AdminPage() {
  const {user}=useAuth();
  if (!user || !["owner","admin"].includes(user.role)) return <div role="alert" className="bg-[#111113]/90 border border-white/10 rounded-2xl p-12 text-center text-white/60"><AlertCircle size={32} className="mx-auto mb-3 text-destructive"/>غير مصرح — للمنظمين والمالك فقط</div>;
  const tool=(id,title,node,ignoreDraft=false)=><AdminTool key={id} id={id} title={title} ignoreDraft={ignoreDraft}>{node}</AdminTool>;
  const renderSection=id=>{
    switch(id){
      case 'overview':return <AdminOverview/>;
      case 'players':return <>{tool('accounts','الحسابات والصلاحيات',<UserRoleManager/>)}{tool('passwords','طلبات استعادة كلمة المرور',<PasswordResetsPanel/>)}{tool('player-reports','بلاغات اللاعبين',<PlayerReportsAdmin/>)}</>;
      case 'clans':return <>{tool('clan-editor','الكلانات والأعضاء والقيادات',<ClanEditor/>)}{tool('plus','اشتراكات Personal Plus وClan Plus — المالك فقط',<OwnerPlusGrants/>)}</>;
      case 'matches':return <>{tool('match-display','خيارات صفحة المباراة',<MatchDisplaySettings/>)}{tool('zoom-rules','قواعد الزوم والصور المعتمدة',<ZoomRulesAdmin/>)}{tool('match-results','المباريات والنتائج والنزاعات',<AdminMatchTools/>)}</>;
      case 'competitions':return <><AdminLinks items={[["/tournaments","إدارة البطولات","التسجيل والقرعة والشجرة وأدوات البطولة الموجودة."],["/leagues","صفحات الدوري","عرض المواسم والترتيب وقوانين كل دوري."]]}/>{tool('leagues','إدارة الدوري والمواسم',<LeaguesManager/>)}</>;
      case 'rank':return <>{tool('rank-system','إعدادات التصنيف وتعريفات الرتب',<RankSystemAdmin/>)}{tool('rr-simulator','محاكي RR — لا يغير البيانات الحقيقية',<RRSimulator/>,true)}</>;
      case 'sanad':return tool('sanad-ai','إعدادات المساعد والحكم والمعرفة والاستهلاك',<SanadAdmin/>);
      case 'rewards':return <>{tool('riv-points','إدارة RIV وسجل عمليات اللاعب',<RivPointsAdmin/>)}{tool('weekly','التحديات الأسبوعية وسجلات المكافآت',<WeeklyChallengesAdmin/>)}{tool('draws','السحوبات والروليت والتدقيق',<DrawManagement/>,true)}</>;
      case 'content':return <><AdminLinks items={[["/news","الأخبار وسوق الانتقالات","مراجعة الأخبار وحذفها من أدوات الإدارة الموجودة."],["/rules","إدارة القوانين","إضافة القوانين وتعديلها وحذفها بالصلاحيات الحالية."]]}/>{tool('tutorials','شروحات Rivals',<TutorialsAdmin/>)}{tool('banners','البنرات والإعلانات',<BannerManager/>)}</>;
      case 'guard':return <>{tool('guard-alerts','تنبيهات RivalsGuard وأدوات المراجعة',<GuardAlertsPanel/>)}{tool('guard-evidence','أدلة وسجلات المباريات',<MatchLogsPanel/>)}</>;
      case 'settings':return <><section className="admin-summary"><h2 className="font-bold mb-3">دليل الإعدادات والأدوات</h2><p className="admin-muted">إعدادات كل نظام داخل قسمه. هذه الروابط توصلك للأدوات الحالية دون تغيير صلاحياتها أو سلوكها.</p><AdminLinks items={[
        ['?section=players','الحسابات والصلاحيات','إدارة اللاعبين وطلبات الحسابات.'],['?section=clans','الكلانات والاشتراكات','القيادات والبيانات ومنح Plus للمالك.'],['?section=competitions','إعدادات المنافسات','الدوري والمواسم والبطولات.'],['?section=rank','إعدادات Rank / RR','Placement والرتب والمواسم والمحاكي.'],['?section=sanad','إعدادات سند','المزود والمساعد والحكم وحدود الاستخدام.'],['?section=guard','أدوات الحماية','التنبيهات والأدلة والمراجعات.'],['?section=rewards','المكافآت والأرصدة','RIV والتحديات والسحوبات.'],['?section=content','المحتوى والإعلانات','الشروحات والأخبار والبنرات.'],['/rules','قوانين المنصة','إدارة القوانين من صفحتها الحالية.']
      ]}/></section></>;
      default:return null;
    }
  };
  return <AdminDashboard key={`${user.id}:${user.role}`} user={user} renderSection={renderSection}/>;
}

function Stat({ label, value, accent, destructive }) {
  let valueColor = "text-white";
  if (accent) valueColor = "text-gold-500";
  if (destructive) valueColor = "text-destructive";
  return (
    <div className="bg-[#111113]/90 border border-white/10 rounded-2xl p-5">
      <div className={`text-xs font-bold uppercase tracking-widest ${destructive ? "text-destructive" : "text-white/40"}`}>{label}</div>
      <div className={`text-3xl font-display font-black ${valueColor} mt-1`}>{value}</div>
    </div>
  );
}
function RivPointsAdmin() {
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [target, setTarget] = useState(null);
  const [amount, setAmount] = useState(1);
  const [reason, setReason] = useState("");
  const [challenge, setChallenge] = useState("attend_and_play");
  const [challenges, setChallenges] = useState([]);
  const [history, setHistory] = useState(null);
  const [busy, setBusy] = useState(false);
  const operation = useRef(null);
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      if (!query.trim()) { setRows([]); return; }
      api.get("/users/search", { params: { q: query, limit: 8 }, signal: controller.signal })
        .then(({data}) => { if (!controller.signal.aborted) setRows(Array.isArray(data) ? data : data.items || []); })
        .catch(() => { if (!controller.signal.aborted) toast.error("تعذر البحث عن اللاعبين"); });
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [query]);
  useEffect(() => {
    api.get("/me/challenges").then(({data}) => setChallenges((data.challenges || []).filter(c => c.id !== "invite_friend"))).catch(() => {});
  }, []);
  useEffect(() => {
    setHistory(null);
    if (!target?.id) return;
    const controller = new AbortController();
    api.get(`/admin/users/${target.id}/riv-points`, { signal: controller.signal })
      .then(({data}) => { if (!controller.signal.aborted) setHistory(data); }).catch(() => {});
    return () => controller.abort();
  }, [target?.id]);
  const submit = async mode => {
    if (busy || !target || reason.trim().length < 3) return toast.error("اختر لاعبًا واكتب السبب أو دليل إنجاز التحدي");
    const delta = Number(amount) * (mode === "subtract" ? -1 : 1);
    if (mode !== "weekly" && (!Number.isInteger(Number(amount)) || Number(amount) < 1 || Number(amount) > 100000)) return toast.error("أدخل عدد نقاط صحيحًا من 1 إلى 100000");
    const signature = JSON.stringify([target.id,delta,reason,mode,challenge]);
    if (operation.current?.signature !== signature) operation.current = { signature, id: crypto.randomUUID() };
    setBusy(true);
    try {
      const path = mode === "weekly" ? `/admin/users/${target.id}/weekly-challenges/${challenge}/approve` : `/admin/users/${target.id}/riv-points`;
      await api.post(path, { delta, reason: reason.trim(), operation_id: operation.current.id });
      operation.current = null;
      toast.success("تم تسجيل العملية وتحديث الرصيد");
      try {
        const {data} = await api.get(`/admin/users/${target.id}/riv-points`);
        setHistory(data);
      } catch { toast.info("تمت العملية؛ أعد اختيار اللاعب لتحديث سجل الرصيد."); }
    } catch(error) { toast.error(formatApiErrorDetail(error.response?.data?.detail) || "تعذر تأكيد العملية؛ أعد المحاولة بنفس البيانات"); }
    finally { setBusy(false); }
  };
  if (!user || !["owner","admin"].includes(user.role)) return null;
  const fieldClass = "w-full bg-black/30 border border-white/15 rounded-xl px-4 py-3 text-sm outline-none focus:border-gold-500 disabled:opacity-40";
  return <section id="riv-points-admin" data-testid="riv-points-admin" className="overflow-hidden rounded-3xl border border-gold-500/30 bg-[#111113] shadow-xl" dir="rtl">
    <div className="p-6 border-b border-white/10 flex gap-4 items-center"><div className="w-12 h-12 rounded-2xl bg-gold-500/15 flex items-center justify-center text-gold-400"><Coins size={25} /></div><div><h2 className="text-xl font-black text-white">إدارة نقاط RIV</h2><p className="text-xs text-white/50 mt-1">اختر اللاعب، ثم أضف النقاط أو اخصمها مع تسجيل السبب.</p></div></div>
    <div className="p-6 grid lg:grid-cols-2 gap-7">
      <div className="space-y-4"><label className="block space-y-2"><span className="text-sm font-bold text-white/70">١. ابحث عن اللاعب</span><input disabled={busy} value={query} onChange={e => setQuery(e.target.value)} placeholder="اسم اللاعب أو حسابه" className={fieldClass} /></label>
        <div className="grid sm:grid-cols-2 gap-2">{rows.map(p => <button type="button" key={p.id} disabled={busy} onClick={() => setTarget(p)} className={`text-right rounded-xl px-4 py-3 border transition ${target?.id === p.id ? "border-gold-500/60 bg-gold-500/10 text-gold-400" : "border-white/10 bg-white/5 hover:border-white/30"}`}><span className="block font-bold text-sm truncate">{p.username}</span><span className="text-[10px] text-white/40">{target?.id === p.id ? "اللاعب المحدد ✓" : "اختيار اللاعب"}</span></button>)}</div>
        {target ? <div className="bg-black/30 rounded-2xl border border-gold-500/20 p-5 flex justify-between items-center gap-3"><div><p className="text-[11px] text-white/45">رصيد اللاعب المحدد</p><p className="font-bold mt-1">{target.username}</p></div><div className="text-3xl font-black text-gold-400 tabular-nums">{history?.riv_points ?? "—"}<span className="text-xs mr-2">RIV</span></div></div> : <div className="rounded-2xl border border-dashed border-white/15 p-6 text-sm text-white/40 text-center">اختر لاعبًا من نتائج البحث لإدارة رصيده.</div>}
        <label className="block space-y-2"><span className="text-sm font-bold text-white/70">٢. عدد النقاط</span><input type="number" min="1" max="100000" value={amount} disabled={busy || !target} onChange={e => setAmount(e.target.value)} className={fieldClass} /></label>
        <label className="block space-y-2"><span className="text-sm font-bold text-white/70">٣. سبب العملية أو دليل إنجاز التحدي</span><input value={reason} maxLength={300} disabled={busy || !target} onChange={e => setReason(e.target.value)} placeholder="مثال: مكافأة تنظيم مباراة" className={fieldClass} /></label>
        <div className="grid grid-cols-2 gap-3"><button type="button" disabled={busy || !target || reason.trim().length < 3} onClick={() => submit("add")} className="rounded-xl bg-gold-500 text-black font-black py-3 hover:bg-gold-400 disabled:opacity-30">+ إضافة نقاط</button><button type="button" disabled={busy || !target || reason.trim().length < 3} onClick={() => submit("subtract")} className="rounded-xl border border-rose-400/30 bg-rose-500/10 text-rose-300 font-bold py-3 hover:bg-rose-500/20 disabled:opacity-30">− خصم نقاط</button></div>
        {busy && <p role="status" className="text-xs text-gold-400">جاري تسجيل العملية...</p>}
      </div>
      <div className="space-y-5"><div className="rounded-2xl border border-white/10 p-5"><h3 className="font-bold mb-4">سجل عمليات اللاعب</h3><div className="max-h-72 overflow-auto space-y-3">{(history?.entries || []).map(e => <div key={e.id} className="flex gap-3 justify-between border-b border-white/5 pb-3"><div className="min-w-0"><p className="text-xs text-white/75 break-words">{e.reason}</p><p className="text-[10px] text-white/35 mt-1">{new Date(e.created_at).toLocaleString("ar-SA")}</p></div><span className={`font-bold text-sm whitespace-nowrap ${e.delta > 0 ? "text-emerald-300" : "text-rose-300"}`}>{e.delta > 0 ? "+" : ""}{e.delta} RIV</span></div>)}{!history?.entries?.length && <p className="text-xs text-white/40 py-7 text-center">{target ? "لا توجد عمليات معروضة لهذا اللاعب." : "اختر لاعبًا لعرض سجله."}</p>}</div></div>
      </div>
    </div>
  </section>;
}
